import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Let the component handle navigation to avoid full page reload
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API methods
export const studentAPI = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
  bulkTransfer: (data) => api.put('/students/bulk-transfer', data),
  bulkImport: (data) => api.post('/students/bulk-import', data),
  export: (params) => api.get('/students/export', { params }),
  getBirthdayStats: () => api.get('/students/birthdays'),
  getSummaryStats: (params) => api.get('/students/summary-stats', { params }),
  getFeeReport: (params) => api.get('/students/fee-report', { params }),
};

export const attendanceAPI = {
  mark: (data) => api.post('/attendance', data),
  get: (params) => api.get('/attendance', { params }),
  getStats: (params) => api.get('/attendance/statistics', { params }),
  getStudentsForAttendance: (params) => api.get('/attendance/students', { params }),
  getMonthlyReport: (params) => api.get('/attendance/monthly-report', { params }),
  getDailyRegister: (params) => api.get('/attendance/daily-register', { params }),
  getCalendarHeatmap: (params) => api.get('/attendance/calendar', { params }),
  getComplianceReport: (params) => api.get('/attendance/compliance', { params }),
};

export const holidayAPI = {
  getAll: (params) => api.get('/holidays', { params }),
  getById: (id) => api.get(`/holidays/${id}`),
  create: (data) => api.post('/holidays', data),
  bulkCreate: (data) => api.post('/holidays/bulk', data),
  generateSundays: (data) => api.post('/holidays/generate-sundays', data),
  update: (id, data) => api.put(`/holidays/${id}`, data),
  delete: (id) => api.delete(`/holidays/${id}`),
  checkDate: (date) => api.get(`/holidays/check/${date}`),
};

export const classAPI = {
  getAll: (params) => api.get('/classes', { params }),
  getById: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post('/classes', data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`),
  getSections: (classId) => api.get(`/classes/${classId}/sections`),
  createSection: (classId, data) => api.post(`/classes/${classId}/sections`, data),
  updateSection: (classId, sectionId, data) => api.put(`/classes/${classId}/sections/${sectionId}`, data),
  deleteSection: (classId, sectionId) => api.delete(`/classes/${classId}/sections/${sectionId}`),
};

export const teacherAssignmentAPI = {
  getAll: (params) => api.get('/teacher-assignments', { params }),
  getMyClasses: () => api.get('/teacher-assignments/my-classes'),
  create: (data) => api.post('/teacher-assignments', data),
  bulkAssign: (data) => api.post('/teacher-assignments/bulk', data),
  delete: (id) => api.delete(`/teacher-assignments/${id}`),
  getTeachersByClass: (params) => api.get('/teacher-assignments/by-class', { params }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getActivities: () => api.get('/dashboard/activities'),
};

export const staffAPI = {
  getAll: (params) => api.get('/staff', { params }),
  getById: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  bulkImport: (data) => api.post('/staff/bulk-import', data),

  // New Staff Features
  getPendingRegistrations: () => api.get('/staff/pending-registrations'),
  approveRegistration: (id) => api.put(`/staff/${id}/approve`),

  applyLeave: (data) => api.post('/staff/leave/apply', data),
  getLeaveApplications: () => api.get('/staff/leave/applications'),
  updateLeaveStatus: (id, data) => api.put(`/staff/leave/${id}/status`, data),

  markAttendance: (data) => api.post('/staff/attendance', data),
  getAttendanceReport: (params) => api.get('/staff/attendance/report', { params }),
  resetPassword: (id, data) => api.put(`/staff/${id}/reset-password`, data),
};

export const feeAPI = {
  getHeads: () => api.get('/fees/heads'),
  createHead: (data) => api.post('/fees/heads', data),
  getStructures: (params) => api.get('/fees/structures', { params }),
  createStructure: (data) => api.post('/fees/structures', data),
  getStudentFees: (studentId) => api.get(`/fees/student/${studentId}`),
  assignFee: (data) => api.post('/fees/assign', data),
  collectFee: (data) => api.post('/fees/pay', data),
  getSummary: (params) => api.get('/fees/summary', { params }),
};

export const semesterAPI = {
  getAll: (params) => api.get('/semesters', { params }),
  get: (id) => api.get(`/semesters/${id}`),
  create: (data) => api.post('/semesters', data),
  update: (id, data) => api.put(`/semesters/${id}`, data),
  delete: (id) => api.delete(`/semesters/${id}`),
};

export const subjectAPI = {
  getAll: (params) => api.get('/subjects', { params }),
  create: (data) => api.post('/subjects', data),
  assignToStudent: (data) => api.post('/subjects/assign-student', data),
  getStudentSubjects: (studentId, params) => api.get(`/subjects/student/${studentId}`, { params }),
  bulkAssign: (data) => api.post('/subjects/bulk-assign', data),
};

export const libraryAPI = {
  getBooks: (params) => api.get('/library/books', { params }),
  getBook: (id) => api.get(`/library/books/${id}`),
  createBook: (data) => api.post('/library/books', data),
  bulkImport: (data) => api.post('/library/books/bulk-import', data),
  updateBook: (id, data) => api.put(`/library/books/${id}`, data),
  deleteBook: (id) => api.delete(`/library/books/${id}`),
  issueBook: (data) => api.post('/library/issue', data),
  returnBook: (issueId, data) => api.post(`/library/return/${issueId}`, data),
  getIssues: (params) => api.get('/library/issues', { params }),
};

export const transportAPI = {
  getVehicles: () => api.get('/transport/vehicles'),
  createVehicle: (data) => api.post('/transport/vehicles', data),
  updateVehicle: (id, data) => api.put(`/transport/vehicles/${id}`, data),
  getRoutes: () => api.get('/transport/routes'),
  createRoute: (data) => api.post('/transport/routes', data),
  updateRoute: (id, data) => api.put(`/transport/routes/${id}`, data),
  getDrivers: () => api.get('/transport/drivers'),
  createDriver: (data) => api.post('/transport/drivers', data),
  updateDriver: (id, data) => api.put(`/transport/drivers/${id}`, data),
  getAllocations: () => api.get('/transport/allocations'),
  allocateTransport: (data) => api.post('/transport/allocations', data),
  getStats: () => api.get('/transport/stats'),
};

export const examAPI = {
  getExams: () => api.get('/exams'),
  createExam: (data) => api.post('/exams', data),
  updateExam: (id, data) => api.put(`/exams/${id}`, data),
  deleteExam: (id) => api.delete(`/exams/${id}`),
  updateMarks: (data) => api.post('/exams/marks', data),
  bulkUpdateMarks: (data) => api.post('/exams/bulk-marks', data),
  getResults: (params) => api.get('/exams/results', { params }),
  getReportCard: (params) => api.get('/exams/report-card', { params }),
  getResultPrintData: (params) => api.get('/exams/result-print', { params }),
  getResultSummary: (params) => api.get('/exams/result-summary', { params }),
  saveResultSummary: (data) => api.post('/exams/result-summary', data),
};

export const reportAPI = {
  getSummary: () => api.get('/reports/summary'),
  getClassAttendance: (date) => api.get('/reports/attendance/class-wise', { params: { date } }),
  getFeeDefaulters: () => api.get('/reports/fees/defaulters'),
  getCumulative: (studentId) => api.get(`/reports/student/${studentId}/cumulative`),
};

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const academicYearAPI = {
  getAll: (params) => api.get('/academic-years', { params }),
  create: (data) => api.post('/academic-years', data),
  update: (id, data) => api.put(`/academic-years/${id}`, data),
  delete: (id) => api.delete(`/academic-years/${id}`),
};

export const certificateAPI = {
  getAll: (params) => api.get('/certificates', { params }),
  create: (data) => api.post('/certificates', data),
  update: (id, data) => api.put(`/certificates/${id}`, data),
  generatePreview: (data) => api.post('/certificates/generate-preview', data),
};

export const noticeAPI = {
  getAll: (params) => api.get('/notices', { params }),
  create: (data) => api.post('/notices', data),
  update: (id, data) => api.put(`/notices/${id}`, data),
  delete: (id) => api.delete(`/notices/${id}`),
};

export const enquiryAPI = {
  getAll: () => api.get('/enquiry'),
  create: (data) => api.post('/enquiry', data),
  update: (id, data) => api.put(`/enquiry/${id}`, data),
  approve: (id) => api.post(`/enquiry/${id}/approve`),
};

export const masterDataAPI = {
  getAll: (params) => api.get('/master-data', { params }),
  create: (data) => api.post('/master-data', data),
  update: (id, data) => api.put(`/master-data/${id}`, data),
  delete: (id) => api.delete(`/master-data/${id}`),
};

export const semesterMappingAPI = {
  getAll: (params) => api.get('/semester-mapping', { params }),
  create: (data) => api.post('/semester-mapping', data),
  delete: (id) => api.delete(`/semester-mapping/${id}`),
  toggle: (id) => api.put(`/semester-mapping/${id}/toggle`),
};

export const cceConfigAPI = {
  getAll: (params) => api.get('/cce-config', { params }),
  getById: (id) => api.get(`/cce-config/${id}`),
  create: (data) => api.post('/cce-config', data),
  update: (id, data) => api.put(`/cce-config/${id}`, data),
  delete: (id) => api.delete(`/cce-config/${id}`),
};

export const authoritySettingsAPI = {
  getAll: () => api.get('/authority-settings'),
  getByRole: (role) => api.get(`/authority-settings/role/${role}`),
  create: (data) => api.post('/authority-settings', data),
  update: (id, data) => api.put(`/authority-settings/${id}`, data),
  delete: (id) => api.delete(`/authority-settings/${id}`),
  initDefaults: () => api.post('/authority-settings/init-defaults'),
};

export const hostelAPI = {
  getAll: () => api.get('/hostel'),
  create: (data) => api.post('/hostel', data),
  update: (id, data) => api.put(`/hostel/${id}`, data),
  delete: (id) => api.delete(`/hostel/${id}`),
  getRooms: () => api.get('/hostel/rooms'),
  createRoom: (data) => api.post('/hostel/rooms', data),
  updateRoom: (id, data) => api.put(`/hostel/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/hostel/rooms/${id}`),
  getAllocations: () => api.get('/hostel/allocations'),
  allocate: (data) => api.post('/hostel/allocations', data),
  deallocate: (id) => api.put(`/hostel/allocations/${id}/deallocate`),
};

export const inventoryAPI = {
  getItems: () => api.get('/inventory/items'),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  getSuppliers: () => api.get('/inventory/suppliers'),
  createSupplier: (data) => api.post('/inventory/suppliers', data),
  updateSupplier: (id, data) => api.put(`/inventory/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/inventory/suppliers/${id}`),
  getTransactions: () => api.get('/inventory/transactions'),
  createTransaction: (data) => api.post('/inventory/transactions', data),
};

export const payrollAPI = {
  getStructures: () => api.get('/payroll/structures'),
  createStructure: (data) => api.post('/payroll/structure', data),
  getSlips: () => api.get('/payroll/slips'),
  generateSlip: (data) => api.post('/payroll/generate-slip', data),
};

export const leaveWorkflowAPI = {
  apply: (data) => api.post('/leave-workflow/apply', data),
  getMyApplications: () => api.get('/leave-workflow/my-applications'),
  getBalance: () => api.get('/leave-workflow/balance'),
  getSubstitutionRequests: () => api.get('/leave-workflow/substitution-requests'),
  confirmSubstitution: (id, data) => api.post(`/leave-workflow/substitution/${id}/confirm`, data),
  hodApprove: (id, data) => api.post(`/leave-workflow/${id}/hod-approve`, data),
  principalApprove: (id, data) => api.post(`/leave-workflow/${id}/principal-approve`, data),
  getForApproval: () => api.get('/leave-workflow/for-approval'),
  getNotifications: () => api.get('/leave-workflow/notifications'),
  manualAssignSubstitution: (subId, data) => api.post(`/leave-workflow/substitution/${subId}/manual-assign`, data),
  markPeriodAsBlank: (subId) => api.post(`/leave-workflow/substitution/${subId}/mark-blank`),
  forwardToHOD: (leaveId) => api.post(`/leave-workflow/${leaveId}/forward-hod`),
  markNotificationRead: (notifId) => api.put(`/leave-workflow/notifications/${notifId}/read`),
};

export const timetableAPI = {
  getSettings: () => api.get('/timetable/settings'),
  saveSettings: (data) => api.post('/timetable/settings', data),
  updateSettings: (id, data) => api.put(`/timetable/settings/${id}`, data),
  getPeriods: (params) => api.get('/timetable/periods', { params }),
  createPeriod: (data) => api.post('/timetable/periods', data),
  updatePeriod: (id, data) => api.put(`/timetable/periods/${id}`, data),
  deletePeriod: (id) => api.delete(`/timetable/periods/${id}`),
  getAllClassTimetables: (params) => api.get('/timetable/class-timetable/all', { params }),
};

export const idCardAPI = {
  getSettings: () => api.get('/idcards/settings'),
  updateSettings: (data) => api.put('/idcards/settings', data),
  generate: (data) => api.post('/idcards/generate', data),
  getStudentCards: (params) => api.get('/idcards/students', { params }),
  getStaffCards: (params) => api.get('/idcards/staff', { params }),
};

export const challanAPI = {
  getAll: (params) => api.get('/challans', { params }),
  getById: (id) => api.get(`/challans/${id}`),
  generate: (data) => api.post('/challans', data),
  verify: (id, data) => api.put(`/challans/${id}/verify`, data),
  cancel: (id) => api.put(`/challans/${id}/cancel`),
};

export const depositAPI = {
  getAll: (params) => api.get('/deposits', { params }),
  getById: (id) => api.get(`/deposits/${id}`),
  getStats: () => api.get('/deposits/stats'),
  create: (data) => api.post('/deposits', data),
  refund: (id, data) => api.put(`/deposits/${id}/refund`, data),
};

export const schoolSettingsAPI = {
  getAll: () => api.get('/school-settings'),
  getBank: () => api.get('/school-settings/bank'),
  update: (data) => api.put('/school-settings', data),
  bulkUpdate: (data) => api.put('/school-settings/bulk', data),
};

export const smsAPI = {
  send: (data) => api.post('/sms/send', data),
  getLogs: (params) => api.get('/sms/logs', { params }),
  getTemplates: () => api.get('/sms/templates'),
};

export const transportFeeAPI = {
  getYearMasters: (params) => api.get('/transport/year-masters', { params }),
  createYearMaster: (data) => api.post('/transport/year-masters', data),
  getPayments: (params) => api.get('/transport/fee-payments', { params }),
  createPayment: (data) => api.post('/transport/fee-payments', data),
  getBusEntries: (params) => api.get('/transport/bus-entries', { params }),
  createBusEntry: (data) => api.post('/transport/bus-entries', data),
};

export const inwardOutwardAPI = {
  getAll: (params) => api.get('/inward-outward', { params }),
  create: (data) => api.post('/inward-outward', data),
  update: (id, data) => api.put(`/inward-outward/${id}`, data),
  delete: (id) => api.delete(`/inward-outward/${id}`),
};

export const appDownloadAPI = {
  getLogs: (params) => api.get('/app-downloads/logs', { params }),
  getStats: () => api.get('/app-downloads/stats'),
};

export const autoRenewalAPI = {
  getStudents: (params) => api.get('/students/auto-renewal', { params }),
  process: (data) => api.post('/students/auto-renewal', data),
  preview: (params) => api.get('/students/auto-renewal/preview', { params }),
};

export default api;
